import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";
import { useChannelCellStyles } from "./ReserveChannelsModal";
import colors from "../../theme/colors";
import { cellIdToString } from "../../utils/labels";
import SelectCell from "../../components/forms/SelectCell";
import { isFsc } from "../../utils/conditions";

export const OFF_TEST_REASONS = ["End of test", "Cell failure"];

// Teardown type that is only selectable once a subscale cell is
// on-test/off-test. FSC (full-scale) cells may select it at any lifecycle
// stage.
const ENGINEERING_TEARDOWN_TYPE = "Engineering";

// Cell statuses at which the "Engineering" teardown type is allowed for
// subscale cells: "N" = On-Test, "F" = Off-Test.
const ENGINEERING_TEARDOWN_ALLOWED_STATUSES: CellStatus[] = ["N", "F"];

/**
 * Returns the teardown-type options that should be selectable for a single
 * cell. "Engineering" is removed unless the cell is at an allowed status, or
 * unless it is the cell's currently-saved value (so existing selections are
 * never clobbered). FSC (full-scale) cells are exempt from the status gate and
 * may select "Engineering" at any point in their lifecycle.
 */
const filterTeardownOptions = (
  options: string[],
  status?: CellStatus | null,
  currentValue?: string | null,
  cellAssembly?: string | null
): string[] => {
  if (
    isFsc(cellAssembly) ||
    (!!status && ENGINEERING_TEARDOWN_ALLOWED_STATUSES.includes(status)) ||
    currentValue === ENGINEERING_TEARDOWN_TYPE
  ) {
    return options;
  }

  return options.filter((option) => option !== ENGINEERING_TEARDOWN_TYPE);
};

export type ModifyTeardownTypesTableProps = {
  teardownSpecsByCondition: {
    [key: string]: {
      teardownTypeByCellId: { [key: string]: string };
      offTestReasonByCellId: { [key: string]: string | null };
      cellStatusByCellId: { [key: string]: CellStatus };
      teardown_flags: { [key: string]: any };
      cellCondition: Condition;
      component_meta_modes: string[];
      cell_statuses_for_condition: string[];
      teardown_condition_component_fields: ConditionUIField[];
    };
  };
  handleChangeTeardownTypes: (
    cellIds: string[],
    newValue: string | null
  ) => void;
  handleChangeOffTestReasons: (
    cellIds: string[],
    newValue: string | null
  ) => void;
  handleChangeTeardownFlags: (
    conditionIds_: string[],
    flag: string,
    newValue: string | boolean | User | null,
    persistEmpty?: boolean
  ) => void;
  teardownTypesToUpdate: { [key: string]: string };
  offTestReasonsToUpdate: { [key: string]: string | null };
  showOffTestReasons: boolean;
  teardownFlagsToUpdate: {
    [key: string]: { [key: string]: string | boolean | User | null };
  };
};

const ModifyTeardownTypesTable = ({
  teardownSpecsByCondition,
  handleChangeTeardownTypes,
  handleChangeOffTestReasons,
  teardownTypesToUpdate,
  offTestReasonsToUpdate,
  showOffTestReasons,
}: ModifyTeardownTypesTableProps) => {
  const classes = useChannelCellStyles();

  return (
    <Table
      className={`dataTable ${classes.root}`}
      size="small"
      style={{ background: colors.striping, width: "auto" }}
    >
      <TableHead>
        <TableRow>
          <TableCell style={{ background: "white" }} />
          {Object.keys(teardownSpecsByCondition).map(
            (str_cell_condition_id, index) => (
              <TableCell
                key={`teardown-types-header${str_cell_condition_id}`}
                style={{
                  background: "white",
                  width: "336px",
                  maxWidth: "336px",
                  minWidth: "272px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {
                  teardownSpecsByCondition[str_cell_condition_id].cellCondition
                    .name
                }
              </TableCell>
            )
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell variant="head">Teardown type</TableCell>
          {Object.keys(teardownSpecsByCondition).map(
            (str_cell_condition_id, index) => {
              return (
                <TableCell
                  style={{ paddingLeft: 0, paddingRight: 0 }}
                  key={`condition-teardowntypes${str_cell_condition_id}`}
                >
                  {Object.keys(
                    teardownSpecsByCondition[str_cell_condition_id]
                      .teardownTypeByCellId
                  ).map((cellId_) => {
                    return (
                      <Box
                        display="flex"
                        style={{ fontSize: 12 }}
                        key={`cell-teardown-types${cellId_}`}
                      >
                        <Box flexGrow={1} alignSelf="center" paddingLeft={4}>
                          {cellIdToString(cellId_)}
                        </Box>
                        <SelectCell
                          key={`select-${index}`}
                          noTable
                          disableClearable
                          options={filterTeardownOptions(
                            teardownSpecsByCondition[str_cell_condition_id]
                              .component_meta_modes,
                            teardownSpecsByCondition[str_cell_condition_id]
                              .cellStatusByCellId[cellId_],
                            teardownTypesToUpdate[cellId_] ||
                              teardownSpecsByCondition[str_cell_condition_id]
                                .teardownTypeByCellId[cellId_],
                            teardownSpecsByCondition[str_cell_condition_id]
                              .cellCondition.cell_assembly
                          )}
                          onChange={(newVal_) => {
                            const newVal = newVal_ as string | null;
                            handleChangeTeardownTypes([cellId_], newVal);
                          }}
                          value={
                            teardownTypesToUpdate[cellId_] ||
                            teardownSpecsByCondition[str_cell_condition_id]
                              .teardownTypeByCellId[cellId_]
                          }
                          style={{ flexGrow: 6 }}
                        />
                      </Box>
                    );
                  })}
                </TableCell>
              );
            }
          )}
        </TableRow>
        {showOffTestReasons && (
          <TableRow>
            <TableCell variant="head">Off-test reason</TableCell>
            {Object.keys(teardownSpecsByCondition).map(
              (str_cell_condition_id, index) => {
                return (
                  <TableCell
                    style={{ paddingLeft: 0, paddingRight: 0 }}
                    key={`condition-teardown-reasons${str_cell_condition_id}`}
                  >
                    {Object.keys(
                      teardownSpecsByCondition[str_cell_condition_id]
                        .offTestReasonByCellId
                    ).map((cellId_) => {
                      return (
                        <Box
                          display="flex"
                          style={{ fontSize: 12 }}
                          key={`cell-teardownreasons${cellId_}`}
                        >
                          <Box flexGrow={1} alignSelf="center" paddingLeft={4}>
                            {cellIdToString(cellId_)}
                          </Box>
                          <SelectCell
                            key={`select-${index}`}
                            noTable
                            options={OFF_TEST_REASONS}
                            onChange={(newVal_) => {
                              const newVal = newVal_ as string | null;
                              handleChangeOffTestReasons([cellId_], newVal);
                            }}
                            value={
                              offTestReasonsToUpdate[cellId_] ||
                              teardownSpecsByCondition[str_cell_condition_id]
                                .offTestReasonByCellId[cellId_] ||
                              null
                            }
                            style={{ flexGrow: 6 }}
                          />
                        </Box>
                      );
                    })}
                  </TableCell>
                );
              }
            )}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default ModifyTeardownTypesTable;
